import { findExperienceChains } from "components/experiences/find-experience-chains";
import { Experience, getFamilyId } from "types/experience";
import { isExperienceItem } from "types/experience-view";
import type { ExperienceViewExperienceItem, ExperienceViewItem } from "types/experience-view";
import { TimeSlot } from "types/time-slot";

interface ExperienceSuggestion {
    id: number;
    name: { [lang: string]: string };
    description: { [lang: string]: string };
    imageReference1x1: string;
    imageReference16x9: string;
}

interface BuildExperienceSuggestionsParams {
    experiences: Experience[];
    /** The experience-view catalogue. Suggestions are a strict subset of what it lists. */
    items: ExperienceViewItem[];
    current: Experience | null | undefined;
    guests: number | null | undefined;
    bookedSectionIds: Set<number>;
    isBookingRequest: boolean;
    limit?: number;
}

interface SuggestionCandidate {
    experience: Experience;
    item: ExperienceViewExperienceItem;
}

const DEFAULT_LIMIT = 3;

/** Start times the party can book, waitlist excluded. Chained experiences yield only whole chains. */
function getBookableStartTimes(
    experience: Experience,
    guests: number | null | undefined
): TimeSlot[] {
    if (!guests) return [];

    return findExperienceChains(experience, guests).startTimes;
}

/**
 * Alternatives for a guest who cannot book the experience they opened: those bookable for the same
 * party size and date, in the order the restaurant configured them. Empty while they can still book
 * what they opened.
 *
 * Candidates are drawn from availableTimes but confirmed against the catalogue, because
 * availableTimes also carries variants whose family is not offered — an active copy under a parent
 * that is not, which the guest can never reach from the list (BOOK-3910).
 */
function buildExperienceSuggestions({
    experiences,
    items,
    current,
    guests,
    bookedSectionIds,
    isBookingRequest,
    limit = DEFAULT_LIMIT,
}: BuildExperienceSuggestionsParams): ExperienceSuggestion[] {
    if (!current || !guests || isBookingRequest) return [];
    if (getBookableStartTimes(current, guests).length > 0) return [];

    const catalogue = new Map<number, ExperienceViewExperienceItem>(
        items.filter(isExperienceItem).map((item) => [item.id, item])
    );
    const currentFamilyId = getFamilyId(current);

    const candidates = experiences
        .filter((experience) => getFamilyId(experience) !== currentFamilyId)
        // Carries the section-filter exclusion, which leaves timeSets unfiltered behind it.
        .filter((experience) => !experience.unavailable)
        .filter(
            (experience) =>
                !experience.activities.some((activity) => bookedSectionIds.has(activity.sectionId))
        )
        .map((experience) => ({ experience, item: catalogue.get(getFamilyId(experience)) }))
        .filter((candidate): candidate is SuggestionCandidate => candidate.item !== undefined)
        .filter(({ experience }) => getBookableStartTimes(experience, guests).length > 0)
        .sort((a, b) => a.experience.sortOrder - b.experience.sortOrder);

    // Two variants of one family are one row to the guest; keep the best-sorted of each.
    const byFamily = new Map<number, ExperienceViewExperienceItem>();
    candidates.forEach(({ item }) => {
        if (!byFamily.has(item.id)) byFamily.set(item.id, item);
    });

    return [...byFamily.values()].slice(0, limit).map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        imageReference1x1: item.imageReference1x1 ?? "",
        imageReference16x9: item.imageReference16x9 ?? "",
    }));
}

export { buildExperienceSuggestions };
export type { ExperienceSuggestion };
